import { useContext } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { CommentCountsContext } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useDocumentScrollHeight } from '@confluence/document-scroll-height/entry-points/useDocumentScrollHeight.ts';

import { FeatureStates } from '../popupStateTypes.ts';

import { useDynamicSplitButton } from './useDynamicSplitButton.ts';

type UseDynamicAIContextReturn = {
	dynamicActionTarget:
		| FeatureStates.Comments
		| FeatureStates.PageCatchup
		| FeatureStates.QuickSummary
		| null;
	queryError?: ApolloErrorInterop;
};

export const useDynamicAIContext = (
	contentId: string,
	contentType: string,
	pageCommentsSectionInView: boolean,
	isLivePage: boolean,
): UseDynamicAIContextReturn => {
	const { scrollY } = useDocumentScrollHeight(isLivePage);

	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const { isAdminHubAIEnabled } = useSessionData();

	const contentTypeEnum = contentType === 'page' ? 'PAGE' : 'BLOGPOST';
	const { showCatchupAsDefault, queryError } = useDynamicSplitButton(contentId, contentTypeEnum);

	if (!isAdminHubAIEnabled) {
		return { dynamicActionTarget: null };
	}

	if (pageCommentsSectionInView && (unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0))
		return { dynamicActionTarget: FeatureStates.Comments };

	if (showCatchupAsDefault && scrollY !== null && scrollY < 400) {
		return { dynamicActionTarget: FeatureStates.PageCatchup };
	}

	if (scrollY !== null && scrollY < 400) {
		return { dynamicActionTarget: FeatureStates.QuickSummary, queryError };
	}

	return { dynamicActionTarget: null, queryError };
};
